// src/hooks/usePermissions.js
import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {isTrypilaDashboard} from '../helpers';

export const usePermissions = () => {
  const {userDetails} = useSelector(state => state?.auth);
  const projectId = sessionStorage.getItem('project_id') || 'add-project';
  const skipRolePermissions = isTrypilaDashboard();

  const USER_PERMISSIONS = useMemo(
    () => userDetails?.permissions,
    [userDetails],
  );
  const USER_COMPANY = useMemo(
    () => userDetails && userDetails?.company,
    [userDetails],
  );

  const permittedLinks = useMemo(
    () =>
      (permissions = {}) => {
        if (skipRolePermissions || USER_PERMISSIONS?.role === 'Owner') {
          return true;
        }
        return Object.values(permissions).some(value => value);
      },
    [USER_PERMISSIONS, skipRolePermissions],
  );

  const permissions = useMemo(
    () => ({
      canViewReconciliation:
        skipRolePermissions ||
        USER_PERMISSIONS?.reconciliationPermissions?.reconciliation,
      canViewIdVerification:
        skipRolePermissions ||
        USER_PERMISSIONS?.onboardPermissions?.viewVerifications,
      canManageFlows:
        skipRolePermissions ||
        USER_PERMISSIONS?.detectPermissions?.manageFlows,
      manageDetectFlows:
        skipRolePermissions ||
        USER_PERMISSIONS?.detectPermissions?.manageFlows,
      reviewCases:
        skipRolePermissions ||
        USER_PERMISSIONS?.detectPermissions?.reviewCases,
      canReviewCases:
        skipRolePermissions ||
        USER_PERMISSIONS?.detectPermissions?.reviewCases,
      canViewHistory:
        skipRolePermissions ||
        USER_PERMISSIONS?.billingPermissions?.viewHistory,
      canViewApiCalls:
        skipRolePermissions || USER_PERMISSIONS?.billingPermissions?.viewLogs,
      managePermissions:
        skipRolePermissions ||
        USER_PERMISSIONS?.settingsPermissions?.managePermissions,
      manageAuditLogs:
        skipRolePermissions ||
        USER_PERMISSIONS?.settingsPermissions?.viewAndDownloadAudit,
      isOwner:
        skipRolePermissions || USER_PERMISSIONS?.role === 'Owner',
      canViewApiLogs:
        skipRolePermissions ||
        USER_PERMISSIONS?.developerPermissions?.viewAndExportLogs,
      canManageApps:
        skipRolePermissions ||
        USER_PERMISSIONS?.developerPermissions?.manageApps,
      canRegenerateKeys:
        skipRolePermissions ||
        USER_PERMISSIONS?.developerPermissions?.regenerateKeys,
      canViewLiveKeys:
        skipRolePermissions ||
        USER_PERMISSIONS?.developerPermissions?.viewLiveKeys,
      canManageTokens:
        skipRolePermissions ||
        USER_PERMISSIONS?.developerPermissions?.manageTokens,
      canManageWebhooks:
        skipRolePermissions ||
        USER_PERMISSIONS?.developerPermissions?.manageWebhooks,
      baasPagePermissions: {
        canViewOverview:
          skipRolePermissions || USER_PERMISSIONS?.baasPermissions?.overview,
        canPayout:
          skipRolePermissions || USER_PERMISSIONS?.baasPermissions?.payout,
        canTransfer:
          skipRolePermissions || USER_PERMISSIONS?.baasPermissions?.transfer,
        canViewPayouts:
          skipRolePermissions ||
          USER_PERMISSIONS?.baasPermissions?.viewPayouts,
        canViewTransactions:
          skipRolePermissions ||
          USER_PERMISSIONS?.baasPermissions?.viewTransactions,
        canViewWallets:
          skipRolePermissions ||
          USER_PERMISSIONS?.baasPermissions?.viewWallets,
        canManageSettings:
          skipRolePermissions ||
          USER_PERMISSIONS?.baasPermissions?.manageSettings,
        canExportWallets:
          skipRolePermissions ||
          USER_PERMISSIONS?.baasPermissions?.exportWallets,
        canExportPayouts:
          skipRolePermissions ||
          USER_PERMISSIONS?.baasPermissions?.exportPayouts,
        canExportTransactions:
          skipRolePermissions ||
          USER_PERMISSIONS?.baasPermissions?.exportTransactions,
      },
    }),
    [USER_PERMISSIONS, skipRolePermissions],
  );

  const webhookPermission = useMemo(
    () =>
      skipRolePermissions ||
      USER_PERMISSIONS?.role === 'Owner' ||
      permissions?.canManageWebhooks,
    [
      skipRolePermissions,
      USER_PERMISSIONS,
      permissions.canManageWebhooks,
    ],
  );

  const modularisation = useMemo(
    () => USER_COMPANY?.modularisation || {},
    [USER_COMPANY],
  );

  const is_partner_company = USER_COMPANY?.is_partner_company;

  return {
    USER_PERMISSIONS,
    permittedLinks,
    ...permissions,
    webhookPermission,
    projectId,

    modularisation: {...modularisation, is_partner_company},
  };
};
